// src/Pages/AiChat.js
import React, { useState, useRef, useEffect } from 'react';
import Header from '../../Static/Header';
import firebase from '../../Firebase/firebaseConnection';
import NextStockTitleMark from '../../components/NextStockTitleMark';
import { ArrowUp, ArrowDown, CornerRightUp, CornerRightDown } from 'react-feather';
import './AiChat.css';

import { useTranslation } from "react-i18next";
import NodeBackend from '../../Resources/NodeBackend';

const backend = new NodeBackend();
const API_ENDPOINT = backend.getPath();

// Recognize explicit tickers and common company names without relying on AI formatting.
const AI_COMPANY_SYMBOLS = {
  'Canadian Imperial Bank of Commerce': 'CM', 'CIBC': 'CM',
  'Canadian Natural Resources': 'CNQ', 'Suncor Energy': 'SU',
  'Berkshire Hathaway': 'BRK.B', 'JPMorgan Chase': 'JPM', 'JPMorgan': 'JPM',
  'Tencent': 'TCEHY', 'Gildan Activewear': 'GIL.TO', 'Enbridge': 'ENB.TO',
  'Definity Financial': 'DFY.TO', 'Alphabet': 'GOOG', 'Google': 'GOOG',
  'Amazon': 'AMZN', 'Apple': 'AAPL', 'Microsoft': 'MSFT',
  'NIO Inc.': 'NIO', 'NIO': 'NIO', 'Nvidia': 'NVDA', 'Tesla': 'TSLA'
};

// Keep the footer independent from the AI wording and omit missing Firebase logos.
function getResponseSymbols(content) {
  const raw = String(content || '');
  const names = Object.keys(AI_COMPANY_SYMBOLS).sort((a, b) => b.length - a.length)
    .map(name => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(
    '(\\$[A-Z]{1,5}(?:\\.(?:TO|B))?\\b|\\((?:(?:NASDAQ|NYSE|TSX):\\s*)?[A-Z]{1,5}(?:\\.(?:TO|B))?\\)|\\b(?:Ticker|Symbol)\\s*:\\s*[A-Z]{1,5}(?:\\.(?:TO|B))?\\b|\\b(?:' + names.join('|') + ')\\b)', 'gi'
  );
  const symbols = [];
  for (const ranked of raw.matchAll(/(?:^|\n)\s*\d+[.)]\s*(?:\*\*)?([A-Z]{1,5}\.(?:TO|V|B))(?:\*\*)?/gm)) {
    if (!symbols.includes(ranked[1])) symbols.push(ranked[1]);
  }
  for (const match of raw.matchAll(pattern)) {
    const token = match[0];
    const company = Object.keys(AI_COMPANY_SYMBOLS).find(name => name.toLowerCase() === token.toLowerCase());
    const symbol = company ? AI_COMPANY_SYMBOLS[company] : token.replace(/^(?:Ticker|Symbol)\s*:\s*/i, '').replace(/[()$]/g, '').replace(/^(?:NASDAQ|NYSE|TSX):\s*/i, '').trim().toUpperCase();
    if (symbol && !symbols.includes(symbol)) symbols.push(symbol);
  }
  // Group cross-listed equivalents into a single company card.
  const unique = new Map();
  symbols.forEach(symbol => {
    const key = symbol.replace(/\.(TO|V|NE|B)$/i, '').toUpperCase();
    if (!unique.has(key) || (symbol.includes('.') && !unique.get(key).includes('.'))) unique.set(key, symbol);
  });
  return Array.from(unique.values()).slice(0, 12);
}

// Cache resolved logos so returning to the chat does not trigger another loading sequence.
const logoSourceCache = new Map();
const ResponseLogo = React.memo(function ResponseLogo({ symbol, onStatus, onOpen, inline = false }) {
  const normalized = symbol === 'T.TO' ? symbol : symbol.replace(/\..*$/, '');
  const urls = React.useMemo(() => ['png', 'jpeg', 'jpg'].map(ext =>
    `https://firebasestorage.googleapis.com/v0/b/otanertech.firebasestorage.app/o/ns-logos%2F${encodeURIComponent(normalized)}.${ext}?alt=media`
  ), [normalized]);
  const [source, setSource] = useState(() => logoSourceCache.get(normalized) || null);
  const [ready, setReady] = useState(() => Boolean(logoSourceCache.get(normalized)));
  useEffect(() => {
    let active = true;
    const cached = logoSourceCache.get(normalized);
    // Retry previously failed logo requests when the chat is mounted again.
    // A transient network failure must not permanently hide search shortcuts.
    if (cached) { setSource(cached); setReady(true); onStatus(symbol, true); return () => { active = false; }; }
    setReady(false);
    let index = 0;
    let timer;
    let image;
    const tryNext = () => {
      if (!active) return;
      if (index >= urls.length) {
        logoSourceCache.delete(normalized);
        onStatus(symbol, false);
        return;
      }
      const url = urls[index++];
      image = new Image();
      const next = () => { clearTimeout(timer); if (active) tryNext(); };
      image.onload = () => {
        clearTimeout(timer);
        if (!active) return;
        logoSourceCache.set(normalized, url);
        setSource(url);
        setReady(true);
        onStatus(symbol, true);
      };
      image.onerror = next;
      timer = setTimeout(next, 2500);
      image.src = url;
    };
    tryNext();
    return () => { active = false; clearTimeout(timer); if (image) { image.onload = null; image.onerror = null; } };
  }, [normalized, symbol, urls, onStatus]);
  if (!ready || !source) return null;
  return (
    <button type="button" className={inline ? "ai-inline-logo-ready" : "ai-response-logo-item is-ready"} onClick={() => onOpen(symbol)} aria-label={`Search ${symbol} in Next Stock`}>
      <span className="ai-response-logo-frame"><img src={source} alt="" /></span>
      {!inline && <span>{symbol}</span>}
      {!inline && <span className="ai-logo-hover-card" role="tooltip"><strong>{symbol}</strong><small>View in Next Stock Search →</small></span>}
    </button>
  );
});

const ResponseLogoStrip = React.memo(function ResponseLogoStrip({ content, onOpen }) {
  const symbols = React.useMemo(() => getResponseSymbols(content), [content]);
  const [status, setStatus] = useState({});
  const onStatus = React.useCallback((symbol, success) => {
    setStatus(previous => previous[symbol] === success ? previous : { ...previous, [symbol]: success });
  }, []);
  if (!symbols.length) return null;
  const hasLoaded = symbols.some(symbol => status[symbol] === true);
  const isPending = symbols.some(symbol => status[symbol] === undefined);
  if (!hasLoaded && !isPending) return null;
  return (
    <div className={`ai-response-logo-strip${hasLoaded ? ' has-logos' : ''}`} aria-label="Company logos">
      <div className="ai-response-logo-row">
        {symbols.map(symbol => <ResponseLogo key={symbol} symbol={symbol} onStatus={onStatus} onOpen={onOpen} />)}
      </div>
    </div>
  );
});

// Image-only logo for clickable stock cards; shares the established logo cache.
function RichStockLogoImage({ symbol }) {
  const normalized = symbol === 'T.TO' ? symbol : symbol.replace(/\..*$/, '');
  const [source, setSource] = useState(() => logoSourceCache.get(normalized) || '');
  useEffect(() => {
    let active = true;
    const cached = logoSourceCache.get(normalized);
    if (cached) { setSource(cached); return () => { active = false; }; }
    const extensions = ['png', 'jpeg', 'jpg'];
    let index = 0;
    let image;
    const next = () => {
      if (!active || index >= extensions.length) return;
      image = new Image();
      const url = `https://firebasestorage.googleapis.com/v0/b/otanertech.firebasestorage.app/o/ns-logos%2F${encodeURIComponent(normalized)}.${extensions[index++]}?alt=media`;
      image.onload = () => { if (active) { logoSourceCache.set(normalized, url); setSource(url); } };
      image.onerror = next;
      image.src = url;
    };
    next();
    return () => { active = false; if (image) { image.onload = null; image.onerror = null; } };
  }, [normalized]);
  return source ? <img src={source} alt="" loading="lazy" /> : <span aria-hidden="true">◈</span>;
}

// Safe, schema-driven response UI. AI data is rendered as React text, never injected as HTML.
function AiRichRenderer({ rich, onOpen }) {
  if (!rich || rich.version !== 1 || !Array.isArray(rich.blocks)) return null;
  const txt = value => String(value == null ? '' : value).slice(0, 4000);
  const metric = (item, index) => <div className={`ai-rich-metric ai-rich-tone-${['positive','negative'].includes(item?.tone) ? item.tone : 'neutral'}`} key={index}><small>{txt(item?.label)}</small><strong>{txt(item?.value)}</strong></div>;
  return <div className="ai-rich">
    {rich.title && <h3 className="ai-rich-title">{txt(rich.title)}</h3>}
    {rich.blocks.slice(0, 12).map((block, index) => {
      if (!block || !block.type) return null;
      switch (block.type) {
        case 'heading': return <h4 className="ai-rich-heading" key={index}>{txt(block.text)}</h4>;
        case 'paragraph': return <p className="ai-rich-paragraph" key={index}>{txt(block.text)}</p>;
        case 'insight': return <aside className="ai-rich-insight" key={index}><strong>✦ {txt(block.title || 'Next Stock Insight')}</strong><p>{txt(block.text)}</p></aside>;
        case 'metrics': return <div className="ai-rich-metrics" key={index}>{(Array.isArray(block.items) ? block.items : []).slice(0, 8).map(metric)}</div>;
        case 'accuracy': return <section className="ai-rich-accuracy" key={index}><div className="ai-rich-accuracy-head"><span className="ai-rich-accuracy-icon">◈</span><span><small>NEXT STOCK RESEARCH</small><h4>Accuracy & Rating Consistency</h4></span></div><div className="ai-rich-metrics">{(Array.isArray(block.items) ? block.items : []).slice(0, 10).map(metric)}</div>{block.text && <p>{txt(block.text)}</p>}</section>;
        case 'list': return <ul className="ai-rich-list" key={index}>{(Array.isArray(block.items) ? block.items : []).slice(0, 15).map((item, j) => <li key={j}>{txt(item)}</li>)}</ul>;
        case 'table': {
          const columns = (Array.isArray(block.columns) ? block.columns : []).slice(0, 8);
          return <div className="ai-rich-table-wrap" key={index}><table className="ai-rich-table"><thead><tr>{columns.map((column,j) => <th key={j}>{txt(column)}</th>)}</tr></thead><tbody>{(Array.isArray(block.rows) ? block.rows : []).slice(0, 30).map((row,j) => <tr key={j}>{columns.map((_,k) => <td key={k}>{txt(Array.isArray(row) ? row[k] : '')}</td>)}</tr>)}</tbody></table></div>;
        }
        case 'stocks': return <section className="ai-rich-stocks" key={index}>{block.title && <h4>{txt(block.title)}</h4>}<div className="ai-rich-stock-grid">{(Array.isArray(block.items) ? block.items : []).slice(0, 12).map((stock,j) => {
          const symbol = String(stock?.symbol || '').trim().toUpperCase();
          if (!/^[A-Z0-9^.-]{1,16}$/.test(symbol)) return null;
          return <button type="button" className="ai-rich-stock" key={j} onClick={() => onOpen(symbol)} aria-label={`Open ${stock.name || symbol} (${symbol}) in Next Stock Search`}><span className="ai-rich-stock-top"><span className="ai-rich-stock-logo"><RichStockLogoImage symbol={symbol} /></span><span><strong>{txt(stock.name || symbol)}</strong><small>{symbol}</small></span><span className="ai-rich-stock-arrow">↗</span></span>{stock.description && <p>{txt(stock.description)}</p>}{Array.isArray(stock.metrics) && <span className="ai-rich-stock-facts">{stock.metrics.slice(0,4).map((m,k) => <span key={k}>{txt(m.label)}: <b>{txt(m.value)}</b></span>)}</span>}<span className="ai-rich-stock-hover-hint" aria-hidden="true">↗ Open in Next Stock Search</span></button>;
        })}</div></section>;
        default: return null;
      }
    })}
  </div>;
}

// Format the AI response without stripping spaces around Markdown markers.
function formatAiResponse(content) {
  let text = String(content || '')
    .replace(/<span\b[^>]*>\s*◆?\s*Powered by\s*(?:<strong>)?Next Stock AI(?:<\/strong>)?\s*<\/span>/gi, '')
    .replace(/(?:◆\s*)?Powered by\s*(?:<strong>)?Next Stock AI(?:<\/strong>)?/gi, '')
    .replace(/<\/?(?:span|strong|div|p|br)\b[^>]*>/gi, '')
    .replace(/\r\n?/g, '\n')
    .replace(/[♧♣♙♟№]/g, '');

  // Accuracy reports arrive as compact Markdown paragraphs. Segment by semantic
  // metrics before the generic list formatter (no rewriting of numerical values).
  const isAccuracy = /(?:Rating\s*Range|Exact\s*Rating|Historical\s*Rating\s*Range)/i.test(text);
  if (isAccuracy) {
    text = text
      .replace(/\s*[-•]\s*(?=(?:\*\*)?(?:Rating\s*Range|Exact\s*Rating)\s*:)/gi, '\n')
      .replace(/([^\n])\s*(?=(?:\*\*)?Historical\s*Rating\s*Range\s*[-–:]?)/gi, '$1\n\n')
      .replace(/(\b(?:20\d{2})\s*[:.])(?=\s*(?:U\.S\.|Canada))/gi, '\n$1 ')
      .replace(/(\d+(?:\.\d+)?%)(?=(?:U\.S\.|Canada|For Exact Rating|Overall combined))/gi, '$1  ')
      .replace(/\s*(?=For Exact Rating[, :])/gi, '\n\n')
      .replace(/\s*(?=These figures describe|The next update is planned)/gi, '\n\n');
  }

  // Repair headings and numbered lists BEFORE Markdown and logo rendering.
  // The AI occasionally concatenates a heading, region label and first list item.
  // Keep list markers on their own lines, including when there is no whitespace.
  text = text
    .replace(/(Highlights|Stocks|Picks|Rankings|Results)(Canada|U\.S\.|USA|United States)(?=\s*(?:\*\*)?\s*1[.)])/gi, '$1\n\n$2')
    .replace(/(\*\*\s*(?:Canada|U\.S\.|USA|United States)\s*\*\*|\b(?:Canada|U\.S\.|USA|United States))\s*(?=1[.)]\s*)/gi, '$1\n')
    .replace(/(\*\*)(?=\s*1[.)]\s*[A-Za-z])/g, '$1\n')
    .replace(/([^\n])\s+(?=[2-9][.)]\s*(?:\*\*)?[A-Za-z])/g, '$1\n');

  // Split region labels out of bold headings that the model merged into one token.
  text = text.replace(/(\*\*[^*\n]{3,100}?)(Canada|U\.S\.|USA)(\*\*)(?=\s*1[.)])/gi, '$1$3\n\n**$2**\n');

  // Recover missing whitespace at Markdown boundaries without changing ordinary prose.
  text = text.replace(/([\p{L}\p{N}])(?=\*\*[^*\n]+\*\*)/gu, '$1 ')
    .replace(/(\*\*[^*\n]+\*\*)(?=[\p{L}\p{N}])/gu, '$1 ');

  // Models sometimes return a heading directly followed by the first numbered item.
  text = text.replace(/(\*\*[^*\n]{3,110}\*\*)(?=\s*(?:\d+[.)]\s*|[-•]\s+))/g, '$1\n\n');
  text = text.replace(/(\b(?:Canada|U\.?S\.?|United States)\s*:?)(?=\s*\d+[.)]\s*)/gi, '$1\n');
  text = text.replace(/(\S)(\s+)(?=\d+[.)]\s*(?:[A-Z][a-z]|\*\*|[A-Z]{2,6}\.TO))/g, '$1\n');

  // Separate consecutive numbered entries, even when supplied as a single paragraph.
  text = text.replace(/([^\n])\s+(?=\d+[.)]\s*(?:\*\*)?(?:[A-Z][a-z]|[A-Z]{2,6}\.TO))/g, '$1\n');
  // Detect category labels that run into a ticker or company, without modifying tickers.
  text = text.replace(/(\b(?:Canada|U\.?S\.?|United States))(?=\s*\d+[.)])/gi, '$1\n');
  text = text.replace(/(\b(?:Canada|U\.?S\.?|United States))(?=\s*(?:GIL\.TO|ENB\.TO|DFY\.TO))/gi, '$1\n');
  text = text.replace(/(\b[A-Z]{2,6}\.TO)(?=[A-Z][a-z])/g, '$1 ');
  text = text.replace(/(\b(?:Upside estimate|Price target|Rating Range|Exact Rating|Historical Rating Range|Overall combined))\s*:/gi, '$1: ');
  text = text.replace(/(\d(?:\.\d+)?%)(?=[A-Za-z])/g, '$1 ');
  // Preserve headings and structured lists instead of introducing special-case reports.
  text = text.replace(/([^\n])\s*(?=\*\*(?:Rating Range|Exact Rating|Historical Rating Range|Overall combined)\s*:)/gi, '$1\n\n');
  // Keep a ranked stock and its associated company/metric on the SAME row.
  // Some AI responses split each bold fragment into its own line; only join
  // continuation lines immediately following a numbered stock ticker.
  const rows = text.split('\n');
  const merged = [];
  const stockRow = /^\s*\d+[.)]\s*(?:\*\*)?[A-Z]{1,6}(?:\.(?:TO|V|B))?(?:\*\*)?(?:\s|$)/i;
  const nextItem = /^\s*(?:\d+[.)]\s|[-•]\s|(?:\*\*)?(?:Canada|U\.S\.|USA)(?:\*\*)?\s*$)/i;
  for (let i = 0; i < rows.length; i++) {
    const current = rows[i];
    if (!stockRow.test(current)) { merged.push(current); continue; }
    let combined = current.trimEnd();
    let j = i + 1;
    while (j < rows.length) {
      const candidate = rows[j].trim();
      if (!candidate || nextItem.test(candidate)) break;
      const isMetric = /^(?:\*\*)?(?:Upside estimate|Price target|Potential upside|[+-]?\d+(?:\.\d+)?%)(?:\*\*)?\s*:?$/i.test(candidate);
      const isCompany = /^(?:\*\*)?[A-Z][\w&.'’ -]{1,75}(?:\*\*)?$/.test(candidate)
        && !/[.!?]\s+[A-Z]/.test(candidate);
      if (!isMetric && !isCompany) break;
      combined += ' ' + candidate;
      j++;
    }
    merged.push(combined);
    i = j - 1;
  }
  text = merged.join('\n');
  return text.replace(/\n{3,}/g, '\n\n').trim();
}

const InlineStockLogo = React.memo(function InlineStockLogo({ symbol, onOpen }) {
  const onStatus = React.useCallback(() => {}, []);
  return <ResponseLogo symbol={symbol} onStatus={onStatus} onOpen={onOpen} inline />;
});

function renderSimpleInline(text, key) {
  return text.split(/(\*\*[^*\n]+?\*\*)/g).filter(Boolean).map((part, i) => {
    const bold = part.startsWith('**') && part.endsWith('**');
    const value = bold ? part.slice(2, -2) : part;
    return bold ? <strong key={`${key}-${i}`}>{value}</strong> : <React.Fragment key={`${key}-${i}`}>{value}</React.Fragment>;
  });
}

// Render text and stock logos as React nodes, avoiding HTML replacement across ticker boundaries.
// Dedicated presentation only for questions explicitly requesting accuracy/history.
// Do not run the generic stock-list heuristics on these reports.
function formatAccuracyReport(content) {
  const raw = String(content || '').replace(/<[^>]*>/g, ' ').replace(/\r/g, '')
    .replace(/(?:◆\s*)?Powered by\s*(?:\*\*)?Next Stock AI(?:\*\*)?/gi, '');
  const plain = raw.replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();
  const title = /Next Stock[’']s reported rating-review results/i.test(plain);
  const cutoff = [...new Set((plain.match(/(?:data dated\s+[^;)]+|(?:current reporting cutoff|latest results cover)\s*:?\s*Q[1-4]\s*20\d{2})/gi) || []))];
  const period = plain.match(/\b(Q[1-4]\s*20\d{2})\b/i)?.[1] || '';
  const percent = '(\\d+(?:\\.\\d+)?\\s*%)';
  const readCountryPair = (part) => {
    if (!part) return null;
    const us = part.match(/\bU\.?S\.?\s*(?:companies)?\s*(?:was|were|:)?\s*(\d+(?:\.\d+)?\s*%)/i)?.[1];
    const canada = part.match(/\bCanada|Canadian/i);
    const ca = canada && part.slice(canada.index).match(/(?:Canada|Canadian(?: companies)?)\s*(?:was|were|:)?\s*(\d+(?:\.\d+)?\s*%)/i)?.[1];
    return us && ca ? [us.replace(/\s/g, ''), ca.replace(/\s/g, '')] : null;
  };
  const rangeBlock = plain.match(/(?:^|[-•]\s*)Rating Range\s*:\s*(.*?)(?=\s*[-•]\s*Exact Rating\s*:|\s*Historical Rating Range|$)/i)?.[1];
  const exactBlock = plain.match(/(?:^|[-•]\s*)Exact Rating\s*:\s*(.*?)(?=\s*Historical Rating Range|\s*[-•]\s*Historical|$)/i)?.[1];
  let rangePair = readCountryPair(rangeBlock);
  let exactPair = readCountryPair(exactBlock);
  const legacy = plain.match(/(?:Latest period\s*[—–-]\s*)?(Q[1-4]\s*20\d{2})\s*:\s*Rating Range was\s*(\d+(?:\.\d+)?%)\s*for U\.S\. companies,?\s*and\s*(\d+(?:\.\d+)?%)\s*for Canadian companies\.?\s*Exact Rating was\s*(\d+(?:\.\d+)?%)\s*and\s*(\d+(?:\.\d+)?%)/i);
  if (legacy) { rangePair = [legacy[2], legacy[3]]; exactPair = [legacy[4], legacy[5]]; }
  const historical = plain.match(/Historical Rating Range\s*(?:combined averages)?\s*:\s*(.*?)(?=\s*[-•]?\s*(?:Historical Exact Rating|20\d{2} Exact Rating|How to read this|These (?:figures|are results)|$))/i);
  const historicalText = historical?.[1] || '';
  const years = [...historicalText.matchAll(/(20\d{2})\s*[,=:]?\s*(\d+(?:\.\d+)?\s*%)/g)].map(m => ({year:m[1], value:m[2].replace(/\s/g, '')}));
  const overall = historicalText.match(/(\d+(?:\.\d+)?%)\s*overall|overall (?:average|combined)(?: is| was|:)?\s*(\d+(?:\.\d+)?%)/i);
  const historicalExact = plain.match(/(?:Historical Exact Rating\s*:?|20\d{2} Exact Rating combined average\s*:)\s*(.*?)(?=\s*How to read this|\s*These (?:figures|are results)|$)/i);
  const exactValue = historicalExact?.[1]?.match(/(?:average (?:was|is)\s*)?(\d+(?:\.\d+)?%)/i)?.[1];
  const how = plain.match(/How to read this\s*:\s*(.*?)(?=These figures describe|$)/i);
  const disclaimer = plain.match(/(?:These figures describe|These are results of Next Stock[’']s historical)[\s\S]*$/i);
  const current = rangePair && exactPair ? [null, period, ...rangePair, ...exactPair] : null;
  // Accept conversational summaries as well as the original rigid report format.
  const naturalRange = plain.match(/Rating Range\s+was\s+(\d+(?:\.\d+)?%)\s+for U\.?S\.? stocks?\s+and\s+(\d+(?:\.\d+)?%)\s+for Canadian stocks?/i);
  const naturalExact = plain.match(/Exact Rating\s+([\d.]+%)\s+U\.?S\.?\s+and\s+([\d.]+%)\s+Canada/i);
  const naturalYears = [...plain.matchAll(/(\d+(?:\.\d+)?%)\s+in\s+(20\d{2})/gi)].map(m => ({year:m[2], value:m[1]}));
  if (!current && naturalRange && naturalExact) {
    rangePair = [naturalRange[1], naturalRange[2]];
    exactPair = [naturalExact[1], naturalExact[2]];
  }
  if (!years.length && naturalYears.length) years.push(...naturalYears);
  const resolvedCurrent = current || (rangePair && exactPair ? [null, period, ...rangePair, ...exactPair] : null);
  // Accept Markdown headings and conversational prose, not just the legacy report template.
  const sectionText = (heading, nextHeadings) => {
    const escaped = heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = raw.match(new RegExp('(?:^|\\n)\\s*#{0,4}\\s*' + escaped + '\\s*[:–-]?\\s*([\\s\\S]*?)(?=\\n\\s*#{1,4}\\s*(?:' + nextHeadings + ')|$)', 'i'));
    return match?.[1]?.replace(/\*\*/g, '').trim() || '';
  };
  const rangeProse = sectionText('Rating Range', 'Exact Rating|Historical Rating Range|Interpretation');
  const exactProse = sectionText('Exact Rating', 'Historical Rating Range|Interpretation');
  const historyProse = sectionText('Historical Rating Range', 'Interpretation');
  const interpretationProse = sectionText('Interpretation', '$^');
  const pairFromProse = (block) => {
    const percentages = [...block.matchAll(/(\d+(?:\.\d+)?\s*%)/g)].map(m => m[1].replace(/\s/g, ''));
    return percentages.length >= 2 ? percentages.slice(0, 2) : null;
  };
  if (!rangePair) rangePair = pairFromProse(rangeProse);
  if (!exactPair) exactPair = pairFromProse(exactProse);
  if (!years.length && historyProse) {
    const yearMatches = [...historyProse.matchAll(/(\d+(?:\.\d+)?%)\s+in\s+(20\d{2})/gi)];
    years.push(...yearMatches.map(m => ({year: m[2], value: m[1]})));
  }
  const proseOverall = historyProse.match(/(\d+(?:\.\d+)?%)\s+overall/i)?.[1];
  const finalCurrent = resolvedCurrent || (rangePair && exactPair ? [null, period, ...rangePair, ...exactPair] : null);
  const hasStructured = !!(finalCurrent || years.length || exactValue || historyProse);
  return { title: title ? 'Next Stock’s Rating Accuracy' : 'Accuracy & Historical Results', cutoff, current: finalCurrent, years,
    overall: overall?.[1] || overall?.[2] || proseOverall, exactValue, historicalText: historicalText || historyProse,
    historicalExactText: historicalExact?.[1], how: how?.[1] || interpretationProse, disclaimer: disclaimer?.[0], hasStructured,
    fallback: raw.replace(/\*\*/g, '').trim() };
}

function AccuracyMetric({ label, value }) {
  return <div className="ai-accuracy-stat"><span className="ai-accuracy-stat-label">{label}</span><strong>{value || '—'}</strong></div>;
}

function renderMessageWithLogos(content, onOpen, accuracyRequest = false) {
  if (accuracyRequest) {
    const report = formatAccuracyReport(content);
    if (!report.hasStructured) return <div className="ai-response-accuracy-report"><div className="ai-accuracy-row ai-accuracy-body">{report.fallback.split(/\n{2,}/).filter(Boolean).map((paragraph, i) => <p key={i}>{paragraph.trim()}</p>)}</div></div>;
    return <div className="ai-response-accuracy-report">
      <div className="ai-accuracy-report-title">📊 {report.title}</div>
      {report.cutoff.length > 0 && <div className="ai-accuracy-caption">{report.cutoff.join(' · ')}</div>}
      {report.current && <section className="ai-accuracy-section">
        <div className="ai-accuracy-section-title">Latest results · {report.current[1]}</div>
        <div className="ai-accuracy-subtitle">Rating Range</div>
        <div className="ai-accuracy-stats"><AccuracyMetric label="U.S." value={report.current[2]}/><AccuracyMetric label="Canada" value={report.current[3]}/></div>
        <div className="ai-accuracy-subtitle">Exact Rating</div>
        <div className="ai-accuracy-stats"><AccuracyMetric label="U.S." value={report.current[4]}/><AccuracyMetric label="Canada" value={report.current[5]}/></div>
      </section>}
      {(report.years.length > 0 || report.historicalText) && <section className="ai-accuracy-section">
        <div className="ai-accuracy-section-title">Historical Rating Range</div>
        {report.years.length > 0 ? <div className="ai-accuracy-stats">{report.years.map(item => <AccuracyMetric key={item.year} label={item.year} value={item.value}/>)}</div> : <p>{report.historicalText}</p>}
        {report.overall && <div className="ai-accuracy-overall">Overall reported average <strong>{report.overall}</strong></div>}
      </section>}
      {report.historicalExactText && <section className="ai-accuracy-section"><div className="ai-accuracy-section-title">Historical Exact Rating</div>
        {report.exactValue && <div className="ai-accuracy-stats"><AccuracyMetric label="Combined average" value={report.exactValue}/></div>}
        <p>{report.historicalExactText}</p>
      </section>}
      {report.how && <section className="ai-accuracy-explanation"><strong>How to read these figures</strong><p>{report.how}</p></section>}
      {report.disclaimer && <p className="ai-accuracy-disclaimer">{report.disclaimer}</p>}
    </div>;
  }
  const clean = formatAiResponse(content);
  const lines = clean.split(/\n/);
  const tickerPattern = /(\b(?:Ticker|Symbol)\s*:\s*[A-Z]{1,5}(?:\.(?:TO|B))?\b|\([A-Z]{1,5}(?:\.(?:TO|B))?\))/g;
  const renderInline = (line, lineIndex) => {
    const rankedTicker = line.match(/^\s*\d+[.)]\s*(?:\*\*)?([A-Z]{1,5}\.(?:TO|V|B))(?=\*\*|\s|$)/);
    const rankedSymbol = rankedTicker && rankedTicker[1];
    // Parse emphasis before injecting ticker logos, so a ticker never splits a **bold** pair.
    const tokens = line.split(/(\*\*[^*\n]+?\*\*)/g).filter(Boolean);
    const rendered = tokens.flatMap((token, tokenIndex) => {
      const bold = token.startsWith('**') && token.endsWith('**');
      const value = bold ? token.slice(2, -2) : token;
      const parts = value.split(tickerPattern).filter(Boolean);
      const children = parts.map((part, partIndex) => {
        const found = part.match(/(?:Ticker|Symbol)\s*:\s*([A-Z]{1,5}(?:\.(?:TO|B))?)|\(([A-Z]{1,5}(?:\.(?:TO|B))?)\)/);
        const symbol = found && (found[1] || found[2]);
        return <React.Fragment key={`${lineIndex}-${tokenIndex}-${partIndex}`}>
          {symbol && <InlineStockLogo symbol={symbol} onOpen={onOpen} />}
          <span dangerouslySetInnerHTML={renderMarkdown(part)} />
        </React.Fragment>;
      });
      return [bold ? <strong key={`${lineIndex}-${tokenIndex}`}>{children}</strong> : <React.Fragment key={`${lineIndex}-${tokenIndex}`}>{children}</React.Fragment>];
    });
    // The logo is inserted after the ranked ticker, not before the company name.
    if (!rankedSymbol) return rendered;
    const tickerEnd = line.indexOf(rankedSymbol) + rankedSymbol.length;
    const before = line.slice(0, tickerEnd);
    const after = line.slice(tickerEnd);
    return <>{renderSimpleInline(before, lineIndex)}<InlineStockLogo symbol={rankedSymbol} onOpen={onOpen} />{renderSimpleInline(after, lineIndex + 10000)}</>;
  };
  // Render Markdown pipe tables as actual tables, preserving inline stock logos.
  const tableRows = new Map();
  const tableHidden = new Set();
  const isPipeRow = line => /^\\s*\\|.+\\|\\s*$/.test(line);
  const isDivider = line => /^\\s*\\|(?:\\s*:?-{3,}:?\\s*\\|)+\\s*$/.test(line);
  for (let i = 0; i < lines.length - 2; i++) {
    if (!isPipeRow(lines[i]) || !isDivider(lines[i + 1])) continue;
    let end = i + 2;
    while (end < lines.length && isPipeRow(lines[end]) && !isDivider(lines[end])) end++;
    if (end === i + 2) continue;
    const parseCells = row => row.trim().replace(/^\\||\\|$/g, '').split('|').map(cell => cell.trim());
    tableRows.set(i, [parseCells(lines[i]), ...lines.slice(i + 2, end).map(parseCells)]);
    for (let j = i + 1; j < end; j++) tableHidden.add(j);
    i = end - 1;
  }
  return <div className={`ai-response-formatted${/(?:Rating\\s*Range|Exact\\s*Rating|Historical\\s*Rating\\s*Range)/i.test(clean) ? ' ai-response-accuracy' : ''}`}>{lines.map((line, index) =>
    tableHidden.has(index) ? null : tableRows.has(index) ?
      <div className="ai-response-table-scroll" key={index}><table className="ai-response-table"><thead><tr>{tableRows.get(index)[0].map((cell, j) => <th key={j}>{renderInline(cell, index * 100 + j)}</th>)}</tr></thead><tbody>{tableRows.get(index).slice(1).map((cells, r) => <tr key={r}>{cells.map((cell, j) => <td key={j}>{renderInline(cell, index * 100 + r * 10 + j)}</td>)}</tr>)}</tbody></table></div> :
    line.trim() ? <div className={`ai-response-line${/^\s*(?:[-*•]\s+|\d+[.)]\s+)/.test(line) ? ' ai-response-list-line' : ''}${/^\s*(?:\*\*)?(?:Rating\s*Range|Exact\s*Rating|Historical\s*Rating\s*Range|Overall combined|For Exact Rating|20\d{2}\s*:)/i.test(line) ? ' ai-response-section-line' : ''}`} key={index}>{renderInline(line, index)}</div> :
      <div className="ai-response-paragraph-gap" key={index} aria-hidden="true" />
  )}</div>;
}

function renderMarkdown(text = '') {
  const escape = value => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  let html = escape(text);
  html = html.replace(/\*\*\s*([^*\n]+?)\s*\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  return { __html: html };
}

export default function AiChat({ setPage }) {

  const { t, i18n } = useTranslation();

  const [messages, setMessages] = useState([]);
  const openStockSearch = React.useCallback(symbol => {
    try { sessionStorage.setItem('ns-search-pending-symbol', symbol); sessionStorage.setItem('ns-search-from-ai', '1'); } catch (_) {}
    setPage('Searchp');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [setPage]);
  const restored = useRef(false);
  const [knownSymbols, setKnownSymbols] = useState([]);
  const extractSymbols = (content) => {
    const matches = [...String(content || '').matchAll(/(?:\$|\b(?:NASDAQ|NYSE|TSX)\s*:\s*|\()([A-Z]{1,5}(?:\.TO)?)(?=\)|\b)/g)].map(x => x[1]);
    return [...new Set(matches)].slice(0, 8);
  };
  useEffect(() => {
    let active = true;
    const unsubscribe = firebase.auth().onAuthStateChanged(async user => {
      if (!user || !active) return;
      try {
        const token = await user.getIdToken();
        const response = await fetch(API_ENDPOINT + 'ai-chat-history', { headers: { Authorization: `Bearer ${token}` } });
        if (!response.ok) return;
        const data = await response.json();
        if (!active || !Array.isArray(data.turns) || !data.turns.length) return;
        restored.current = true;
        setMessages(data.turns.flatMap(turn => [{role:'user', content:turn.question}, {role:'assistant', content:turn.answer, rich:turn.rich}]));
        setAiHasResponded(true);
        setShowSuggestions(false);
        requestAnimationFrame(() => setTimeout(() => {
          const container = chatEndRef.current?.parentElement;
          container?.scrollTo({ top: container.scrollHeight, behavior: 'auto' });
        }, 240));
      } catch (e) { console.warn('AI history unavailable', e); }
    });
    return () => { active = false; unsubscribe(); };
  }, []);
  
   useEffect(() => {
    if (restored.current) return;
    setMessages([
      {
        role: "assistant",
        content: t("aichat.welcomeMsg")
      }
    ]);
  }, [i18n.language]);

    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const chatEndRef = useRef(null);
    const [showSuggestions, setShowSuggestions] = useState(true);
    const [aiHasResponded, setAiHasResponded] = useState(false);


// Keep the latest assistant response visible, including restored conversations.
useEffect(() => {
    const container = chatEndRef.current?.parentElement;
    if (!container || !messages.length) return;
    const scrollToLatest = () => { container.scrollTop = container.scrollHeight; };
    const frame = requestAnimationFrame(scrollToLatest);
    // Allow rendered markdown and logo strips to settle after the new message.
    const timer = setTimeout(scrollToLatest, 120);
    return () => { cancelAnimationFrame(frame); clearTimeout(timer); };
}, [messages, loading]);

// Grow the composer with its content, keeping long messages scrollable.
const inputRef = useRef(null);
useEffect(() => {
    const node = inputRef.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${Math.min(node.scrollHeight, 160)}px`;
}, [input]);

    async function handleSend(customMessage) {
        // 🧹 Ensure input is a clean string
        let rawMessage = typeof customMessage === 'string' ? customMessage : input;
        if (typeof rawMessage !== 'string') rawMessage = ''; // fallback

        // 🧽 Normalize whitespace and remove funky characters
        const trimmed = rawMessage
            .replace(/\r?\n\s*\r?\n/g, '\n') // remove excessive blank lines
            .replace(/target=\s*_blank/gi, "")
            .replace(/rel=\s*noopener\s*noreferrer/gi, "")
            .replace(/>\s*watch/gi, "> watch")
            .trim();

        if (!trimmed || loading) return;

        const userMessage = { role: 'user', content: trimmed };
        const newMessages = [...messages, userMessage];

        setMessages(newMessages);
        setInput('');
        setLoading(true);

        try {
            const token = await firebase.auth().currentUser?.getIdToken();
            const res = await fetch(API_ENDPOINT + 'ai-chat-with-web', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                body: JSON.stringify({
                    behaviorId: 'assistant',
                    messages: newMessages.map((m) => ({
                        role: m.role,
                        content: m.content,
                    })),
                }),
            });

            const data = await res.json();

            if (!data.success) {
                console.error('AI error:', data.error);
                setMessages((prev) => [
                    ...prev,
                    {
                        role: 'assistant',
                        // role: 'sales',
                        content:
                            "Sorry, I had trouble retrieving the information. Please try again in a few seconds.",
                    },
                ]);
                setAiHasResponded(true);
            } else {
                setMessages((prev) => [
                    ...prev,
                    { role: 'assistant', content: data.reply, rich: data.rich },
                ]);
            }
        } catch (err) {
            console.error(err);
            setMessages((prev) => [
                ...prev,
                {
                    role: 'assistant',
                    content:
                        "Sorry, there was a connection error. Please check your internet or try again shortly.",
                },
            ]);
        } finally {
            setLoading(false);
            setShowSuggestions(false);
        }
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }




return (
  <div className="ai-chat-page">
    
    <Header setPage={setPage} />

    <div className="ai-chat-main">
      <div className="ai-chat-heading"><NextStockTitleMark className="ai-chat-heading-logo" /><h2 className="ai-chat-title">{t("aichat.title")}</h2></div>

      <div className={`ai-chat-window ${aiHasResponded ? 'expand-smooth' : ''}`}>
        
        {/* === Chat Messages === */}
        <div className="ai-chat-messages">
          {messages.map((msg, index) => (
            <div
              key={index}
              className={
                'ai-chat-bubble ' +
                (msg.role === 'user'
                  ? 'ai-chat-bubble-user'
                  : 'ai-chat-bubble-assistant')
              }
            >
              {msg.role === 'assistant' ? (
                <div className="ai-chat-response-content">
                  <div className="ai-chat-markdown">{msg.rich?.version === 1 && Array.isArray(msg.rich.blocks) && msg.rich.blocks.length ? <AiRichRenderer rich={msg.rich} onOpen={openStockSearch} /> : renderMessageWithLogos(msg.content, openStockSearch, /\b(?:accuracy|accurate|accurately|historical|historically|history|rating[ -]?range|exact[ -]?rating)\b/i.test(messages.slice(0, index).reverse().find(item => item.role === 'user')?.content || ''))}</div>
                  <div className="ai-response-powered">Powered by <strong>Next Stock AI</strong></div>
                  {!(msg.rich?.version === 1 && Array.isArray(msg.rich.blocks) && msg.rich.blocks.length) && <ResponseLogoStrip content={msg.content} onOpen={openStockSearch} />}
                </div>
              ) : (
                msg.content
              )}
            </div>
          ))}

          {loading && (
            <div className="ai-chat-bubble ai-chat-bubble-assistant ai-chat-thinking" role="status" aria-live="polite">
              <div className="ai-thinking-cosmos" aria-hidden="true"><i/><i/><i/><span>✦</span></div>
              <div className="ai-thinking-copy"><strong>{t("aichat.thinking")}</strong><span className="ai-thinking-progress"><i/><i/><i/></span><small>Analyzing your request and preparing insights</small></div>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* === Conversation Starters === */}
        <div className={`starter-section${showSuggestions ? "" : " starter-collapsed"}`}>
          
          {/* 🔽 Toggle button */}
          <button
            className="starter-toggle-btn"
            type="button"
            aria-label={showSuggestions ? "Hide conversation starters" : "Show conversation starters"}
            aria-expanded={showSuggestions}
            onClick={() => setShowSuggestions(!showSuggestions)}
          >
            {showSuggestions ? <CornerRightDown size={16}/> : <CornerRightUp size={16}/>}
          </button>

          {showSuggestions && (
            <>
              <div className="starter-carousel-wrapper">
                <div className="starter-carousel">
                  {[
                    t("aichat.suggestion1"),
                    t("aichat.suggestion2"),
                    t("aichat.suggestion3"),
                    t("aichat.suggestion4"),
                    t("aichat.suggestion5"),
                    t("aichat.suggestion6"),
                    t("aichat.suggestion7"),
                    t("aichat.suggestion8"),
                    t("aichat.suggestion9"),
                    t("aichat.suggestion10"),
                    t("aichat.suggestion11"),
                    t("aichat.suggestion12")
                  ].map((text, i) => (
                    <button
                      key={i}
                      className="starter-btn"
                      onClick={() => handleSend(text)}
                    >
                      {text}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        {/* === Input Area === */}
        <div className="ai-new-input-area">
          <div className="ai-input-inner">
            <textarea
              className="ai-input-field"
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t("aichat.placeholder")}
              rows={1}
            />

            <button
              className="ai-input-send"
              onClick={handleSend}
              disabled={loading}
            >
              <ArrowUp size={18}/>
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
);



}
